import React, { useEffect } from 'react';
import classNames from 'classnames';
import { X } from 'lucide-react';

import css from './Sheet.module.css';

/**
 * Sheet component - Mobile slide-in panel matching Radix UI Dialog pattern
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Whether the sheet is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {React.Node} props.children - Sheet content
 * @returns {JSX.Element}
 */
const Sheet = ({ open, onOpenChange, children }) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={css.sheet}>
      <div className={css.overlay} onClick={() => onOpenChange(false)} />
      <div className={css.content}>
        {children}
      </div>
    </div>
  );
};

/**
 * SheetTrigger component - Button that opens the sheet
 */
const SheetTrigger = ({ children, asChild, onClick }) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: (e) => {
        onClick?.(e);
        children.props.onClick?.(e);
      },
    });
  }

  return (
    <button type="button" onClick={onClick} className={css.trigger}>
      {children}
    </button>
  );
};

/**
 * SheetContent component - The sheet panel content
 */
const SheetContent = ({ children, side = 'left', className }) => {
  return (
    <div className={classNames(css.contentPanel, css[`side-${side}`], className)}>
      {children}
    </div>
  );
};

/**
 * SheetHeader component - Header section of sheet
 */
const SheetHeader = ({ children, className }) => {
  return (
    <div className={classNames(css.header, className)}>
      {children}
    </div>
  );
};

/**
 * SheetTitle component - Title in sheet header
 */
const SheetTitle = ({ children, className }) => {
  return (
    <h2 className={classNames(css.title, className)}>
      {children}
    </h2>
  );
};

/**
 * SheetDescription component - Description in sheet
 */
const SheetDescription = ({ children, className }) => {
  return (
    <p className={classNames(css.description, className)}>
      {children}
    </p>
  );
};

/**
 * SheetClose component - Close button
 */
const SheetClose = ({ onClick, children }) => {
  return (
    <button type="button" onClick={onClick} className={css.close}>
      {children || <X className={css.closeIcon} />}
    </button>
  );
};

Sheet.Trigger = SheetTrigger;
Sheet.Content = SheetContent;
Sheet.Header = SheetHeader;
Sheet.Title = SheetTitle;
Sheet.Description = SheetDescription;
Sheet.Close = SheetClose;

export default Sheet;
